import type { DenialCategory } from './denialNormalization';
import { DENIAL_CATEGORY_LABELS } from './denialNormalization';

export interface AppealPlaceholders {
  patientInitials: string;
  authorizationId: string;
  caseNumber: string;
  dateOfService: string;
  cptCodes: string;
  modifiers: string;
  diagnosisCodes: string;
  clinicalRationale: string;
  attachedEvidence: string[];
  payerName: string;
  planName: string;
  denialDate: string;
  denialReason: string;
  surgeonName: string;
  facilityName: string;
}

export interface AppealTemplate {
  id?: string;
  payer_id?: string;
  plan_id?: string;
  denial_category: DenialCategory;
  template_name: string;
  template_content: string;
  checklist_items: string[];
  version: number;
}

// Default templates by denial category
export const DEFAULT_APPEAL_TEMPLATES: Record<DenialCategory, Omit<AppealTemplate, 'id' | 'payer_id' | 'plan_id'>> = {
  MEDICAL_NECESSITY: {
    denial_category: 'MEDICAL_NECESSITY',
    template_name: 'Medical Necessity Appeal',
    template_content: `Dear Medical Director,

RE: Appeal for Prior Authorization Denial - Medical Necessity
Patient: {{patientInitials}}
Authorization ID: {{authorizationId}}
Case Number: {{caseNumber}}
Date of Service: {{dateOfService}}
Procedure(s): {{cptCodes}} {{modifiers}}
Diagnosis: {{diagnosisCodes}}

We are writing to appeal the denial of prior authorization dated {{denialDate}} for the above-referenced patient.

CLINICAL RATIONALE:
{{clinicalRationale}}

The requested procedure is medically necessary based on:
1. The patient's clinical presentation and documented symptoms
2. Failure of conservative treatment measures
3. Current evidence-based clinical guidelines
4. Expected improvement in patient outcomes

ATTACHED DOCUMENTATION:
{{attachedEvidence}}

Based on the clinical evidence presented, we respectfully request that you reconsider this denial and approve the requested authorization.

Sincerely,
{{surgeonName}}
{{facilityName}}`,
    checklist_items: [
      'Clinical notes documenting medical necessity',
      'Conservative treatment documentation',
      'Relevant imaging studies',
      'Peer-reviewed literature support',
      'Letter of medical necessity from treating physician',
    ],
    version: 1,
  },
  
  DOCUMENTATION_MISSING: {
    denial_category: 'DOCUMENTATION_MISSING',
    template_name: 'Documentation Submission Appeal',
    template_content: `Dear Prior Authorization Department,

RE: Appeal - Additional Documentation Submission
Patient: {{patientInitials}}
Authorization ID: {{authorizationId}}
Case Number: {{caseNumber}}
Date of Service: {{dateOfService}}
Procedure(s): {{cptCodes}} {{modifiers}}

We are responding to your request for additional documentation dated {{denialDate}}.

Please find enclosed the following requested documentation:
{{attachedEvidence}}

CLINICAL SUMMARY:
{{clinicalRationale}}

We believe the enclosed documentation satisfies the requirements for authorization. Please contact us if any additional information is needed.

Sincerely,
{{surgeonName}}
{{facilityName}}`,
    checklist_items: [
      'Previously missing clinical notes',
      'Updated operative report',
      'Additional imaging studies',
      'Physical therapy notes if applicable',
      'Referral documentation',
    ],
    version: 1,
  },
  
  CODING_MODIFIER: {
    denial_category: 'CODING_MODIFIER',
    template_name: 'Coding Correction Appeal',
    template_content: `Dear Claims Review Department,

RE: Appeal - Coding/Modifier Correction
Patient: {{patientInitials}}
Authorization ID: {{authorizationId}}
Case Number: {{caseNumber}}
Date of Service: {{dateOfService}}
Original Procedure(s): {{cptCodes}} {{modifiers}}

We are writing to appeal the denial dated {{denialDate}} related to coding/modifier issues.

CODING CLARIFICATION:
{{clinicalRationale}}

The submitted codes accurately reflect the procedures performed based on:
1. Operative report documentation
2. CPT coding guidelines
3. Modifier usage per payer-specific rules

ATTACHED DOCUMENTATION:
{{attachedEvidence}}

We request reconsideration of this denial based on the corrected/clarified coding information.

Sincerely,
{{surgeonName}}
{{facilityName}}`,
    checklist_items: [
      'Detailed operative report',
      'Coding rationale documentation',
      'CPT/modifier reference guidelines',
      'Unbundling justification if applicable',
    ],
    version: 1,
  },
  
  COVERAGE_LIMITATION: {
    denial_category: 'COVERAGE_LIMITATION',
    template_name: 'Coverage Exception Appeal',
    template_content: `Dear Appeals Department,

RE: Request for Coverage Exception
Patient: {{patientInitials}}
Authorization ID: {{authorizationId}}
Case Number: {{caseNumber}}
Date of Service: {{dateOfService}}
Procedure(s): {{cptCodes}} {{modifiers}}
Diagnosis: {{diagnosisCodes}}

We are requesting an exception to the coverage limitation cited in the denial dated {{denialDate}}.

EXCEPTION JUSTIFICATION:
{{clinicalRationale}}

This request is based on:
1. The unique clinical circumstances of this patient
2. Lack of alternative covered treatment options
3. Expected clinical benefit
4. Potential cost savings from preventing complications

ATTACHED DOCUMENTATION:
{{attachedEvidence}}

We request that you consider this exception based on the clinical merits of the case.

Sincerely,
{{surgeonName}}
{{facilityName}}`,
    checklist_items: [
      'Letter of medical necessity',
      'Documentation of alternative treatment attempts',
      'Clinical evidence supporting exception',
      'Cost-benefit analysis if available',
    ],
    version: 1,
  },
  
  TIMELY_FILING: {
    denial_category: 'TIMELY_FILING',
    template_name: 'Timely Filing Appeal',
    template_content: `Dear Appeals Department,

RE: Timely Filing Appeal
Patient: {{patientInitials}}
Authorization ID: {{authorizationId}}
Case Number: {{caseNumber}}
Date of Service: {{dateOfService}}
Procedure(s): {{cptCodes}}

We are appealing the timely filing denial dated {{denialDate}}.

REASON FOR DELAY:
{{clinicalRationale}}

Supporting documentation demonstrating timely submission attempt:
{{attachedEvidence}}

We request that you waive the timely filing requirement based on the circumstances documented above.

Sincerely,
{{surgeonName}}
{{facilityName}}`,
    checklist_items: [
      'Proof of original submission attempt',
      'Documentation of system/process issues',
      'Correspondence with payer',
      'Timeline of events',
    ],
    version: 1,
  },
  
  NON_COVERED_SERVICE: {
    denial_category: 'NON_COVERED_SERVICE',
    template_name: 'Non-Covered Service Appeal',
    template_content: `Dear Medical Director,

RE: Appeal for Non-Covered Service Determination
Patient: {{patientInitials}}
Authorization ID: {{authorizationId}}
Case Number: {{caseNumber}}
Date of Service: {{dateOfService}}
Procedure(s): {{cptCodes}} {{modifiers}}
Diagnosis: {{diagnosisCodes}}

We are appealing the non-covered service determination dated {{denialDate}}.

APPEAL RATIONALE:
{{clinicalRationale}}

We believe this service should be covered because:
1. The procedure is standard of care for the diagnosis
2. It is not experimental or investigational
3. It meets medical necessity criteria
4. Similar procedures are covered under the plan

ATTACHED DOCUMENTATION:
{{attachedEvidence}}

We request reconsideration of this determination.

Sincerely,
{{surgeonName}}
{{facilityName}}`,
    checklist_items: [
      'Evidence-based literature',
      'FDA approval documentation',
      'Professional society guidelines',
      'Peer-reviewed studies',
    ],
    version: 1,
  },
  
  OTHER: {
    denial_category: 'OTHER',
    template_name: 'General Appeal',
    template_content: `Dear Appeals Department,

RE: Appeal of Authorization Denial
Patient: {{patientInitials}}
Authorization ID: {{authorizationId}}
Case Number: {{caseNumber}}
Date of Service: {{dateOfService}}
Procedure(s): {{cptCodes}} {{modifiers}}
Diagnosis: {{diagnosisCodes}}

We are writing to appeal the denial dated {{denialDate}}.

Denial Reason: {{denialReason}}

APPEAL RATIONALE:
{{clinicalRationale}}

ATTACHED DOCUMENTATION:
{{attachedEvidence}}

We respectfully request reconsideration of this denial.

Sincerely,
{{surgeonName}}
{{facilityName}}`,
    checklist_items: [
      'Clinical notes',
      'Relevant supporting documentation',
      'Letter of medical necessity',
    ],
    version: 1,
  },
};

export function generateAppealContent(
  template: string,
  placeholders: Partial<AppealPlaceholders>
): string {
  let content = template;
  
  const replacements: Record<string, string> = {
    '{{patientInitials}}': placeholders.patientInitials || '[Patient Initials]',
    '{{authorizationId}}': placeholders.authorizationId || '[Authorization ID]',
    '{{caseNumber}}': placeholders.caseNumber || '[Case Number]',
    '{{dateOfService}}': placeholders.dateOfService || '[Date of Service]',
    '{{cptCodes}}': placeholders.cptCodes || '[CPT Codes]',
    '{{modifiers}}': placeholders.modifiers || '',
    '{{diagnosisCodes}}': placeholders.diagnosisCodes || '[Diagnosis Codes]',
    '{{clinicalRationale}}': placeholders.clinicalRationale || '[Enter clinical rationale here]',
    '{{attachedEvidence}}': placeholders.attachedEvidence?.map(e => `• ${e}`).join('\n') || '[List attached evidence]',
    '{{payerName}}': placeholders.payerName || '[Payer Name]',
    '{{planName}}': placeholders.planName || '[Plan Name]',
    '{{denialDate}}': placeholders.denialDate || '[Denial Date]',
    '{{denialReason}}': placeholders.denialReason || '[Denial Reason]',
    '{{surgeonName}}': placeholders.surgeonName || '[Surgeon Name]',
    '{{facilityName}}': placeholders.facilityName || '[Facility Name]',
  };
  
  for (const [placeholder, value] of Object.entries(replacements)) {
    content = content.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
  }
  
  return content;
}
